import { BooleanInput } from '@angular/cdk/coercion';
import { NgClass, NgIf } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatDividerModule } from '@angular/material/divider';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { Router } from '@angular/router';
import { UserService } from 'app/core/user/user.service';
import { User } from 'app/core/user/user.types';
import { Subject, takeUntil, firstValueFrom } from 'rxjs';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { FuseDrawerService } from '@fuse/components/drawer/drawer.service';
import { NavigationService } from 'app/core/navigation/navigation.service';
import { NgFor } from '@angular/common';
import { CommandPaletteComponent } from '@shared/components/command-palette/command-palette.component';
import { SharedComponentModule } from '@shared/components/shared-component.module';

@Component({
    selector       : 'user',
    templateUrl    : './user.component.html',
    encapsulation  : ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    exportAs       : 'user',
    standalone     : true,
    imports        : [MatButtonModule, MatMenuModule, NgIf, MatIconModule, NgClass, MatDividerModule, MatDialogModule, NgFor, SharedComponentModule],
})
export class UserComponent implements OnInit, OnDestroy
{
    /* eslint-disable @typescript-eslint/naming-convention */
    static ngAcceptInputType_showAvatar: BooleanInput;
    /* eslint-enable @typescript-eslint/naming-convention */

    @Input() showAvatar: boolean = true;
    user: User;
    microservicios: any[] = [];

    private _unsubscribeAll: Subject<any> = new Subject<any>();


    /**
     * Constructor
     */
    constructor(
        private _changeDetectorRef: ChangeDetectorRef,
        private _router: Router,
        private _userService: UserService,
        private _dialog: MatDialog,
        private _fuseDrawerService: FuseDrawerService,
        private _navigationService: NavigationService,
    )
    {
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Lifecycle hooks
    // -----------------------------------------------------------------------------------------------------

    /**
     * On init
     */
    ngOnInit(): void
    {
        // Subscribe to user changes
        this._userService.user$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((user: User) =>
            {
                this.user = user;

                // Mark for check
                this._changeDetectorRef.markForCheck();
            });
    }

    /**
     * On destroy
     */
    ngOnDestroy(): void
    {
        // Unsubscribe from all subscriptions
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Update the user status
     *
     * @param status
     */
    updateUserStatus(status: string): void
    {
        // Return if user is not available
        if ( !this.user )
        {
            return;
        }

        // Update the user
        this._userService.update({
            ...this.user,
            status,
        }).subscribe();
    }

    /**
     * Cambiar Contraseña
     */
    cambiarPassword(): void
    {
        this._router.navigate(['/change-password']);
    }

    /**
     * Redirige al listado de tickets de soporte
     */
    cargarTicket(): void
    {
        this._router.navigate(['/soporte/listado']);
    }

    /**
     * Carga el estado de los microservicios externos
     * Se dispara solo cuando se abre el menú
     */
    async loadMicroserviciosStatus(): Promise<void> {
        try {
            const data = await firstValueFrom(this._navigationService.getEstadoMicroservicios());
            
            this.microservicios = Object.entries(data).map(([key, value]: any) => ({
                nombre: value.label,
                activo: value.activo
            }));

            this._changeDetectorRef.markForCheck();
        } catch (error) {
            console.error('Error al cargar estados de microservicios', error);
        }
    }

    /**
     * Sign out
     */
    signOut(): void
    {
        this._router.navigate(['/sign-out']);
    }

    /**
     * Abre el buscador rápido de módulos
     */
    openBuscadorModulos(): void {
        if (this._dialog.openDialogs.some(d => d.componentInstance instanceof CommandPaletteComponent)) {
            return;
        }

        this._dialog.open(CommandPaletteComponent, {
            width: '600px',
            panelClass: 'custom-dialog-container',
            backdropClass: 'blur-backdrop'
        });
    }
}
